import type { Post, PostsPage } from '@/types/posts'

/**
 * Optimistic reordering for the AT Protocol pinned slot.
 *
 * Clicking "Pin to profile" has to move the post to the top of the Posts tab
 * *immediately*, not only once the AppView confirmation lands a few seconds
 * later. The earlier fix flipped `pinnedEntry` on the row in place so the banner
 * appeared, but left the row where it was — so the post visibly jumped to the
 * top later, out of step with the click that caused it.
 *
 * Moving rows is only safe because of how the AppView actually answers
 * `getAuthorFeed` with `includePins`:
 *
 * - It prepends the pinned post as a `reasonPin` item **and drops that URI from
 *   the chronological items of the same page**, so page one holds exactly one
 *   row per URI (verified live: 25 items, no duplicate URIs).
 * - That dedup is per-response, so a *deeper* page can still carry the
 *   chronological copy of the pinned post. Two rows for one URI is therefore a
 *   real, already-shipped state — and it is fine, because `PostList` keys a
 *   pinned row as `pinned-${uri}` and a chronological row as `uri`, and
 *   `threadGrouping` refuses to group a `pinnedEntry` row at all.
 *
 * So the collision risk is not "the same post appears twice" — that is normal.
 * It is only ever "the same *kind* of row appears twice for one URI":
 *
 * - two `pinned-${uri}` rows — impossible here, only page one gets a pinned row
 *   and only one row on it is ever promoted;
 * - two plain `uri` rows — possible if a demoted row were re-inserted into page
 *   one while a deeper page still held its chronological copy. That is the one
 *   case this module actively guards (`laterUris` below).
 */

/** Feed-ordering timestamp, or undefined when it cannot be parsed. */
function orderingTime(post: Post): number | undefined {
    if (!post.createdAt) return undefined
    const time = Date.parse(post.createdAt)
    return Number.isNaN(time) ? undefined : time
}

/**
 * Where `post` belongs in a reverse-chronological array: the first slot whose
 * occupant is strictly older. Ties place the incoming row *after* the existing
 * ones, so the move is deterministic and never fights a stable server sort.
 * Returns undefined when `post` carries no usable timestamp.
 */
function chronologicalIndex(posts: Post[], post: Post): number | undefined {
    const time = orderingTime(post)
    if (time === undefined) return undefined
    for (let i = 0; i < posts.length; i++) {
        const other = orderingTime(posts[i])
        if (other !== undefined && other < time) return i
    }
    return posts.length
}

/**
 * AT Protocol allows exactly one pin, so pinning X un-pins whatever this author
 * had pinned before. Mirrors the pre-reorder predicate exactly.
 */
function isSupersededPin(candidate: Post, target: Post): boolean {
    return (
        !target.isActivityPub &&
        !candidate.isActivityPub &&
        candidate.uri !== target.uri &&
        candidate.author?.did === target.author?.did &&
        (!!candidate.pinnedEntry || candidate.isPinned === true)
    )
}

/** True when `post` sorts strictly after the last row currently on page one. */
function fallsPastPageOne(posts: Post[], post: Post): boolean {
    const last = posts[posts.length - 1]
    if (!last) return false
    const lastTime = orderingTime(last)
    const time = orderingTime(post)
    return lastTime !== undefined && time !== undefined && time < lastTime
}

function findRow(pages: PostsPage[], uri: string): Post | undefined {
    for (const page of pages) {
        const found = page.posts.find(p => p.uri === uri)
        if (found) return found
    }
    return undefined
}

/** A row that lost the pinned slot, remembered with where it used to sit. */
interface Demoted {
    post: Post
    originalIndex: number
}

/**
 * Apply an optimistic pin/unpin across every cached page of one feed.
 *
 * Page one is reordered (that is where the AppView puts the pinned slot).
 * Deeper pages only get their `isPinned` state corrected — their rows are
 * chronological copies whose position does not change when a pin moves.
 */
export function applyOptimisticPin(pages: PostsPage[], target: Post, next: boolean): PostsPage[] {
    if (pages.length === 0) return pages

    const flipStateOnly = (p: Post): Post => {
        if (p.uri === target.uri) return { ...p, isPinned: next }
        if (next && isSupersededPin(p, target)) return { ...p, isPinned: false }
        return p
    }

    // Mastodon returns the pinned copy *and* the chronological copy and never
    // dedupes (see `mastodonService`), so moving or re-keying its rows would
    // collide list keys outright. It needs no optimism either: its write is
    // synchronous and `onSuccess` invalidates immediately.
    if (target.isActivityPub) {
        return pages.map(page => ({ ...page, posts: page.posts.map(flipStateOnly) }))
    }

    const deeperPages = pages
        .slice(1)
        .map(page => ({ ...page, posts: page.posts.map(flipStateOnly) }))

    // True when a chronological slot exists past the end of page one — either a
    // loaded deeper page, or an unfetched one page one's cursor still points at.
    const hasPagesBeyondPageOne = deeperPages.length > 0 || pages[0].cursor != null

    return [
        reorderPageOne(pages[0], target, next, deeperPages, hasPagesBeyondPageOne),
        ...deeperPages,
    ]
}

function reorderPageOne(
    page: PostsPage,
    target: Post,
    next: boolean,
    deeperPages: PostsPage[],
    hasPagesBeyondPageOne: boolean
): PostsPage {
    const laterUris = new Set<string>()
    for (const deeper of deeperPages) {
        for (const row of deeper.posts) laterUris.add(row.uri)
    }

    const rest: Post[] = []
    const demoted: Demoted[] = []
    let promoted: Post | undefined

    page.posts.forEach(p => {
        const isTarget = p.uri === target.uri
        if (!isTarget && !(next && isSupersededPin(p, target))) {
            rest.push(p)
            return
        }
        if (isTarget && next) {
            promoted = { ...p, isPinned: true, pinnedEntry: true }
            return
        }
        // Losing the pin. Only a row that actually *occupies* the pinned slot moves;
        // a plain chronological row that merely carries derived `isPinned` is already
        // in its right place and just loses the flag.
        if (p.pinnedEntry) {
            demoted.push({
                post: { ...p, isPinned: false, pinnedEntry: undefined },
                originalIndex: rest.length,
            })
            return
        }
        rest.push({ ...p, isPinned: false })
    })

    if (next && !promoted) {
        // Pinned from somewhere other than page one — a deeper page, the Replies tab,
        // or a post detail view. The AppView would still prepend a pinned row to page
        // one, and the chronological copy (if any) stays where it is, so do the same.
        promoted = {
            ...(findRow(deeperPages, target.uri) ?? target),
            isPinned: true,
            pinnedEntry: true,
        }
    }

    const posts = rest
    for (const { post, originalIndex } of demoted) {
        // The chronological copy already lives on a deeper page (only page one is
        // deduped against the pinned slot), so re-adding it here would put two rows
        // under the same `uri` list key.
        if (laterUris.has(post.uri)) continue

        const index = chronologicalIndex(posts, post)
        if (index === undefined) {
            // No usable timestamp to place it by — degrade to leaving it where it was
            // rather than guessing, so the row never disappears.
            posts.splice(Math.min(originalIndex, posts.length), 0, post)
            continue
        }
        // Strictly older than the last row on page one, with more pages behind it: its
        // chronological slot is on a page past this one, which is exactly what the
        // server's next page-one response will show. Dropping it here is what the
        // confirmed page does moments later anyway, just without the intervening frame
        // where the row sits in a slot it does not belong in. Ties are *not* dropped —
        // an equal timestamp is not evidence the row fell off the page.
        if (hasPagesBeyondPageOne && index === posts.length && fallsPastPageOne(posts, post)) {
            continue
        }
        posts.splice(index, 0, post)
    }

    if (promoted) posts.unshift(promoted)

    return { ...page, posts }
}
